import { useQuery } from "@tanstack/react-query";
import { berserkr, isSupabaseConfigured } from "../lib/supabase";

export interface FilingRow {
  id: string;
  case_number: string;
  court: string;
  jurisdiction: string;
  caption: string | null;
  filing_kind: string;
  title: string;
  filed_at: string | null;
  filed_by: string | null;
  is_operative: boolean;
  source_uri: string | null;
  created_at: string;
}

export interface FilingSectionRow {
  id: string;
  filing_id: string;
  section_kind: string;
  ordinal: number | null;
  title: string | null;
  legal_basis: string | null;
}

export interface FilingParagraphRow {
  id: string;
  filing_id: string;
  section_id: string;
  paragraph_number: number;
  body: string;
  target: string | null;
}

/**
 * All filings for a given case workspace. Looks up the case's
 * case_number first, then queries the visibility-gated filings view
 * (security_invoker, scoped via filing_scopes + user_can_see).
 */
export function useCaseFilings(caseId: string | undefined) {
  return useQuery<FilingRow[], Error>({
    queryKey: ["case-filings", caseId],
    enabled: isSupabaseConfigured() && !!caseId,
    queryFn: async () => {
      if (!caseId) return [];

      const { data: caseRow, error: caseErr } = await berserkr()
        .from("cases")
        .select("case_number")
        .eq("id", caseId)
        .maybeSingle();
      if (caseErr) throw new Error(caseErr.message);
      if (!caseRow) return [];

      const { data: filings, error: fErr } = await berserkr()
        .from("filings")
        .select(
          "id, case_number, court, jurisdiction, caption, filing_kind, title, filed_at, filed_by, is_operative, source_uri, created_at",
        )
        .eq("case_number", (caseRow as { case_number: string }).case_number)
        .order("filed_at", { ascending: false });
      if (fErr) throw new Error(fErr.message);

      return (filings ?? []) as FilingRow[];
    },
  });
}

/**
 * Sections of one filing, ordered as the document presents them.
 * Useful for the Raven dossier's "structure" panel.
 */
export function useFilingSections(filingId: string | undefined) {
  return useQuery<FilingSectionRow[], Error>({
    queryKey: ["filing-sections", filingId],
    enabled: isSupabaseConfigured() && !!filingId,
    queryFn: async () => {
      if (!filingId) return [];
      const { data, error } = await berserkr()
        .from("filing_sections")
        .select("id, filing_id, section_kind, ordinal, title, legal_basis")
        .eq("filing_id", filingId)
        .order("ordinal", { ascending: true, nullsFirst: false });
      if (error) throw new Error(error.message);
      return (data ?? []) as FilingSectionRow[];
    },
  });
}
